import { useMemo, useState } from "react";
import type { MatrixClient, Room } from "matrix-js-sdk";
import { Text } from "../../theme/primitives";
import { avatarHttpUrl, getOtherMember, isGroupRoom, listRoomMembers } from "../../matrix/rooms";
import { useRoomTimeline } from "./useRoomTimeline";
import { usePresenceTyping } from "./usePresenceTyping";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { Composer } from "./Composer";

interface ChatPaneProps {
  client: MatrixClient;
  roomId: string;
  onBack?: () => void;
}

/** Right-hand chat pane for the selected room (1:1 or group). */
export function ChatPane({ client, roomId, onBack }: ChatPaneProps) {
  const room = client.getRoom(roomId);
  const [error, setError] = useState<string | null>(null);

  const group = useMemo(() => (room ? isGroupRoom(client, room) : false), [client, room]);
  const other = useMemo(
    () => (room && !group ? getOtherMember(client, room) : null),
    [client, room, group],
  );
  const avatarUrl = useMemo(
    () => (group ? null : avatarHttpUrl(client, other?.avatarMxc ?? null)),
    [client, other?.avatarMxc, group],
  );

  if (!room) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Text muted>Conversation not found.</Text>
      </div>
    );
  }

  const me = client.getUserId();
  const groupTitle =
    room.name ||
    listRoomMembers(room)
      .filter((m) => m.userId !== me)
      .map((m) => m.displayName)
      .join(", ");

  return (
    <ChatPaneInner
      client={client}
      room={room}
      roomId={roomId}
      onBack={onBack}
      error={error}
      setError={setError}
      isGroup={group}
      memberCount={listRoomMembers(room).length}
      title={group ? groupTitle || roomId : other?.displayName || room.name || roomId}
      avatarUrl={avatarUrl}
      otherUserId={other?.userId ?? null}
    />
  );
}

interface InnerProps {
  client: MatrixClient;
  room: Room;
  roomId: string;
  onBack?: () => void;
  error: string | null;
  setError: (m: string | null) => void;
  isGroup: boolean;
  memberCount: number;
  title: string;
  avatarUrl: string | null;
  otherUserId: string | null;
}

function ChatPaneInner({
  client,
  room,
  roomId,
  onBack,
  error,
  setError,
  isGroup,
  memberCount,
  title,
  avatarUrl,
  otherUserId,
}: InnerProps) {
  const { messages, loadingOlder, canLoadMore, loadOlder } = useRoomTimeline(client, room);
  const presenceTyping = usePresenceTyping(client, room, otherUserId);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      <ChatHeader
        client={client}
        room={room}
        title={title}
        avatarUrl={avatarUrl}
        isGroup={isGroup}
        memberCount={memberCount}
        otherUserId={otherUserId}
        presenceTyping={presenceTyping}
        onBack={onBack}
      />

      {error && (
        <div
          role="alert"
          style={{
            background: "var(--color-danger)",
            color: "var(--color-text-inverse)",
            padding: "var(--space-sm) var(--space-lg)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text style={{ color: "inherit" }}>{error}</Text>
          <button
            onClick={() => setError(null)}
            style={{ background: "transparent", border: "none", color: "inherit", fontSize: 16 }}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      <MessageList
        client={client}
        room={room}
        messages={messages}
        loadingOlder={loadingOlder}
        canLoadMore={canLoadMore}
        onLoadOlder={loadOlder}
        otherUserId={otherUserId}
      />

      <Composer client={client} roomId={roomId} onError={setError} />
    </div>
  );
}
